import { motion } from 'framer-motion';
import React from 'react';
import { FaAppStoreIos, FaGooglePlay } from 'react-icons/fa';
import { FiExternalLink, FiLinkedin, FiMail, FiMapPin, FiPhone, FiTwitter } from 'react-icons/fi';
import { Link } from 'react-router-dom';

export const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    platform: {
      title: 'المنصة',
      links: [
        { label: 'الرئيسية', href: '/', isInternal: true },
        { label: 'خدماتنا', href: '/services', isInternal: true },
        { label: 'القيادة', href: '/leadership', isInternal: true },

      ]
    },
    support: {
      title: 'الوظائف',
      links: [
        { label: 'التقديم', href: 'mailto:info@alkhreej.com', isInternal: false },
      ]
    },
    legal: {
      title: 'قانوني',
      links: [
        { label: 'سياسة الخصوصية', href: 'https://docs.google.com/document/u/0/d/1y7CvrCBxOCinBUOKnMgWq0UtiSe6yEEAMlTHtlD_0q8/mobilebasic', isInternal: false },
        { label: 'شروط الاستخدام', href: 'https://docs.google.com/document/d/1mQpqKLB3p_jzbH8Jg-OID2hRYm8uvcCEbzMTK5LW2Eg/edit?tab=t.0', isInternal: false },
      ]
    }
  };

  const socialLinks = [
    { icon: FiLinkedin, href: 'https://sa.linkedin.com/company/alkhreej', label: 'LinkedIn' },
    { icon: FiTwitter, href: 'https://x.com/d_alkhreej?lang=en', label: 'Twitter' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <footer className="bg-government-navy text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '60px 60px'
        }} />
      </div>

      <div className="section-container relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="py-16"
        >
          {/* Main Footer Content */}
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8 mb-12">
            {/* Company Info */}
            <motion.div variants={itemVariants} className="lg:col-span-1">
              <div className="flex items-center mb-6">
                <img 
                  src="/logo_1to1.png" 
                  alt="ضوء الخريج" 
                  className="h-10 w-auto ml-3 object-contain rounded-xl"
                  style={{ aspectRatio: 'auto' }}
                />
                <span className="text-2xl font-bold font-heading">ضوء الخريج</span>
              </div>
              
              <p className="text-gray-300 mb-6 leading-relaxed">
                المنصة الرائدة في المملكة العربية السعودية لتوجيه الطلاب مهنياً 
                وربطهم بالفرص التعليمية والمهنية المناسبة.
              </p>

              {/* Contact Info */}
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <FiMail size={16} className="text-brand-secondary" />
                  <a 
                    href="mailto:info@alkhreej.com" 
                    className="text-gray-300 hover:text-white transition-colors duration-200"
                  >
                    info@alkhreej.com
                  </a>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <FiPhone size={16} className="text-brand-secondary" />
                  <a 
                    href="tel:+966599444984" 
                    className="text-gray-300 hover:text-white transition-colors duration-200"
                  >
                    984 444 599 966+
                  </a>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-300">
                  <FiMapPin size={16} className="text-brand-secondary" />
                  <span>الرياض، المملكة العربية السعودية</span>
                </div>
              </div>
            </motion.div>

            {/* Footer Links */}
            {Object.entries(footerLinks).map(([key, section]) => (
              <motion.div key={key} variants={itemVariants}>
                <h3 className="text-lg font-bold mb-6 text-brand-secondary font-heading">
                  {section.title}
                </h3>
                <ul className="space-y-3">
                  {section.links.map((link, index) => (
                    <li key={index}>
                      {link.isInternal ? (
                        <Link
                          to={link.href}
                          className="text-gray-300 hover:text-white transition-colors duration-200 text-sm"
                        >
                          {link.label}
                        </Link>
                      ) : (
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-300 hover:text-white transition-colors duration-200 text-sm"
                        >
                          {link.label}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
                
                {/* Download buttons for Platform section */}
                {key === 'platform' && (
                  <div className="mt-6 space-y-3">
                    <div className="text-sm font-medium text-brand-secondary mb-3">حمّل التطبيق</div>
                    <div className="space-y-2">
                      <a 
                        href="https://apps.apple.com/us/app/%D8%B6%D9%88%D8%A1-%D8%A7%D9%84%D8%AE%D8%B1-%D9%8A%D8%AC/id1109148279"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-gray-800 hover:bg-gray-700 text-white px-3 py-2 rounded-lg flex items-center gap-2 transition-all duration-300 hover:scale-105 text-sm w-full"
                      >
                        <FaAppStoreIos size={16} className="flex-shrink-0" />
                        <div className="text-right">
                          <div className="text-[10px] opacity-90">حمّل من</div>
                          <div className="text-xs font-semibold">App Store</div>
                        </div>
                      </a>
                      
                      <a 
                        href="https://play.google.com/store/apps/details?id=sarah.com.graduateguide&hl=en"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-gray-800 hover:bg-gray-700 text-white px-3 py-2 rounded-lg flex items-center gap-2 transition-all duration-300 hover:scale-105 text-sm w-full"
                      >
                        <FaGooglePlay size={14} className="flex-shrink-0" />
                        <div className="text-right">
                          <div className="text-[10px] opacity-90">حمّل من</div>
                          <div className="text-xs font-semibold">Google Play</div>
                        </div>
                      </a>
                      
                      <a 
                        href="https://alkhreej.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-brand-primary hover:bg-brand-primary/90 text-white px-3 py-2 rounded-lg flex items-center gap-2 transition-all duration-300 hover:scale-105 text-sm w-full"
                      >
                        <FiExternalLink size={14} className="flex-shrink-0" />
                        <div className="text-center">
                          <div className="text-[10px] opacity-90">استخدم على</div>
                          <div className="text-xs font-semibold">الويب</div>
                        </div>
                      </a>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Bottom Section */}
          <motion.div
            variants={itemVariants}
            className="pt-8 border-t border-gray-700"
          >
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              {/* Copyright */}
              <div className="text-sm text-gray-400">
                <p>
                  © {currentYear} ضوء الخريج. جميع الحقوق محفوظة.
                </p>
                <p className="mt-1">
                  مرخص من وزارة التجارة والاستثمار - ترخيص رقم: 7043439889
                </p>
              </div>

              {/* Social Links */}
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-400 ml-4">تابعنا على:</span>
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="w-10 h-10 bg-gray-700 hover:bg-brand-primary rounded-full flex items-center justify-center text-gray-300 hover:text-white transition-all duration-300 hover:scale-110"
                  >
                    <social.icon size={20} />
                  </a>
                ))}
              </div>
            </div>
          </motion.div>

         
        </motion.div>
      </div>
    </footer>
  );
};